// View selection reads the complete catalog. These descriptions describe source
// content, never rules about which question should activate a source.
export function describeViewSource(source) {
  const text = String(source?.description || source?.body || source?.parts?.join(' ') || source?.title || '')
    .replace(/\s+/g, ' ').trim();
  if (text.length <= 420) return text;
  const prefix = text.slice(0, 421);
  const sentenceEnd = Math.max(prefix.lastIndexOf('. '), prefix.lastIndexOf('! '), prefix.lastIndexOf('? '), prefix.lastIndexOf('。'));
  if (sentenceEnd >= 200) return prefix.slice(0, sentenceEnd + 1);
  return `${prefix.slice(0, 417).replace(/\s+\S*$/, '')}…`;
}

export function createViewCatalog(index) {
  const sources = (index?.sources || []).filter((source) => !source.search_only).map((source) => ({
    source_id: source.source_id, id: source.id, route: source.route, page: source.page,
    priority: source.priority || 0, selected: Boolean(source.selected),
    title: source.title, anchor: source.anchor || '',
    description: describeViewSource(source), body: source.body, parts: source.parts || [],
    view_kind: source.view_kind, presentations: source.presentations || ['cite'],
    component_description: source.component_description || '',
    narrative_guidance: source.narrative_guidance || '',
    topics: source.topics || [], media: source.media || [], entity: source.entity || null,
  }));
  const interactions = (index?.rich_blocks || []).flatMap((block) => {
    const parent = sources.find((source) => source.route === block.route && !source.anchor && source.view_kind === 'prose')
      || sources.find((source) => source.entity?.richBlock === block.id);
    const toSource = (entry, variantOf = '') => ({
      source_id: entry.id, route: entry.route || block.route, page: entry.page || block.title, title: entry.title,
      anchor: entry.anchor || '', description: describeViewSource(entry),
      body: [entry.description, ...(entry.publicParts || [])].join('\n\n'),
      parts: entry.publicParts?.length ? [entry.description, ...entry.publicParts] : [entry.description, (entry.topics || []).join(', ')].filter(Boolean),
      component_description: entry.componentDescription || entry.description,
      narrative_guidance: entry.narrativeGuidance || '',
      priority: entry.priority ?? parent?.priority ?? 0, selected: Boolean(parent?.selected),
      topics: entry.topics || [],
      view_kind: (entry.kind || block.kind) === 'interactive' ? 'interactive' : 'embed',
      presentations: [entry.allowedPresentation || block.allowedPresentation], media: entry.media || [],
      entity: entry.entity || null, rich: true,
      companion_for: parent?.source_id || '',
      component_family: entry.componentFamily || block.componentFamily || block.id,
      ...(variantOf ? { variant_of: variantOf } : {}),
    });
    return [
      toSource(block),
      ...(block.variants || []).map((variant) => toSource({ ...block, ...variant, variants: undefined }, block.id)),
    ];
  });
  return [...new Map([...sources, ...interactions].map((source) => [source.source_id, source])).values()];
}

export function viewMediumMetadata(source) {
  const presentations = Array.isArray(source?.presentations) ? source.presentations.filter(Boolean) : [];
  const mediaCount = Array.isArray(source?.media) ? source.media.length : 0;
  const kind = source?.view_kind || 'prose';
  const evidenceMode = kind === 'gallery'
    ? 'visual_record'
    : ['interactive', 'embed'].includes(kind)
    ? 'interactive_evidence'
    : mediaCount
    ? 'text_and_visual_evidence'
    : 'textual_evidence';
  const originalComponent = presentations.some((item) => ['component', 'interactive', 'embed', 'media'].includes(item));
  const contentScope = [...new Set([
    ...(Array.isArray(source?.topics) ? source.topics : []),
    ...(Array.isArray(source?.media) ? source.media.map((item) => item?.alt) : []),
  ].map((item) => String(item || '').trim()).filter(Boolean))].slice(0, 120);
  return {
    evidence_mode: evidenceMode,
    media_count: mediaCount,
    part_count: Array.isArray(source?.parts) ? source.parts.length : 0,
    presentations,
    original_component: originalComponent,
    content_scope: contentScope,
    ...(source?.component_description ? { component_description: source.component_description } : {}),
    ...(source?.narrative_guidance ? { editorial_role: source.narrative_guidance } : {}),
  };
}

function periodFromBody(source) {
  const text = String(source?.body || source?.parts?.[0] || '').trim();
  const match = text.match(/^([A-Z][a-z]+\s+\d{4}\s+-\s+(?:[A-Z][a-z]+\s+\d{4}|Present))\b/);
  return match ? match[1] : '';
}

export function viewTemporalMetadata(source) {
  const period = String(source?.entity?.period || periodFromBody(source)).trim();
  const status = String(source?.entity?.status || '').trim();
  if (!period) return status ? { status } : {};
  return {
    period,
    ...(status ? { status } : {}),
  };
}

export function viewSelectionDescriptor(source) {
  return {
    source_id: source.source_id,
    title: source.title,
    page: source.page,
    priority: source.priority || 0,
    selected: Boolean(source.selected),
    kind: source.view_kind || 'prose',
    description: describeViewSource(source),
    ...viewTemporalMetadata(source),
    medium: viewMediumMetadata(source),
    ...(source.companion_for ? { companion_for: source.companion_for } : {}),
    ...(source.component_family ? { component_family: source.component_family } : {}),
    ...(source.variant_of ? { variant_of: source.variant_of } : {}),
  };
}

export function viewSelectionCatalog(sources) {
  const variantsByParent = new Map();
  for (const source of sources) {
    if (!source.variant_of) continue;
    const variants = variantsByParent.get(source.variant_of) || [];
    variants.push(source);
    variantsByParent.set(source.variant_of, variants);
  }
  return sources.filter((source) => !source.variant_of).map((source) => {
    const descriptor = viewSelectionDescriptor(source);
    const variants = variantsByParent.get(source.source_id) || [];
    if (!variants.length) return descriptor;
    return {
      ...descriptor,
      variants: variants.map((variant) => {
        const medium = viewMediumMetadata(variant);
        return {
          source_id: variant.source_id,
          title: variant.title,
          state: `Focused on ${variant.title} with its pin and location photograph open.`,
          content_scope: medium.content_scope,
          media_count: medium.media_count,
        };
      }),
    };
  });
}
